import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  InputAdornment,
} from "@mui/material";
import Email from "@mui/icons-material/MailOutline";
import Password from "@mui/icons-material/Key";
import Grid from "@mui/material/Grid";
import { LoginModalProps } from "../../interfaces/global-interfaces";
import { colors } from "../../constants/colors";
import {
  sendPasswordResetOtp,
  confirmPasswordReset,
  loginWithEmail,
} from "../../redux/actions/auth-action";
import { useDispatch } from "react-redux";
import {
  setIsLoggedIn,
  setLoginDetails,
} from "../../redux/reducers/auth-reducer";
import { networkService } from "../../networking/network-services";
import { setMobileNumber } from "../../redux/reducers/service-reducer";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import { startUserSession } from "../../utils/analyticsTracker";
import ActiveLoginSessions, { RawSession } from "../ActiveLoginSessions";

const MAX_ACTIVE_DEVICES = 3;

const LoginModal: React.FC<LoginModalProps> = ({
  onHide,
  onUsernameUpdate,
}) => {
  const dispatch = useDispatch();
  const [isForgotPassword, setForgotPassword] = React.useState<any>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [mobileNumber, setStateMobileNumber] = React.useState("");
  const [emailId, setEmailID] = React.useState("");
  const [emailIdForgot, setEmailIdForgot] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [forgotStep, setForgotStep] = React.useState<1 | 2>(1);
  const [errorForgotEmailMessage, setForgotEmailMessage] = React.useState("");
  const [errorOtpMessage, setOtpMessage] = React.useState("");
  const [errorNewPasswordMessage, setNewPasswordMessage] = React.useState("");
  const [errorConfirmPasswordMessage, setConfirmPasswordMessage] = React.useState("");
  const [errorLoginEmailMessage, setLoginEmailMessage] = React.useState("");
  const [errorLoginPasswordMessage, setLoginPasswordMessage] = React.useState("");
  const [emailLogin, setEmailLogin] = React.useState(true);
  const [resendTimer, setResendTimer] = useState<number>(120);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionData, setSessionData] = useState<RawSession[]>([]);
  const [sessionManagementToken, setSessionManagementToken] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setTimeout(() => {
        setResendTimer((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    }
  }, [resendTimer]);


  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;
    setEmailID(emailValue);
    if (!emailRegex.test(emailValue)) {
      setLoginEmailMessage("Please Enter a Valid Email Id");
    } else {
      setLoginEmailMessage("");
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = event.target.value;
    if (passwordValue === "") {
      setLoginPasswordMessage("Please Enter a Password");
    } else {
      setLoginPasswordMessage("");
    }
    setPassword(passwordValue);
  };

  const handleLoginWithEmail = async () => {
    setLoading(true);
    try {
      if (!emailRegex.test(emailId)) {
        setLoginEmailMessage("Please Enter a Valid Email Id");
        setLoading(false);
      }
      if (password === "") {
        setLoginPasswordMessage("Please Enter a Password");
        setLoading(false);
        return;
      }
      const res = await loginWithEmail({ email: emailId, password });
      if (res.status === 200) {
        setLoading(false);
        localStorage.setItem("loginDate", new Date().toISOString().split("T")[0]);
        localStorage.setItem("loginAt", String(Date.now()));
        dispatch(setIsLoggedIn(true));
        dispatch(setLoginDetails(res?.data));
        dispatch(setMobileNumber(mobileNumber));
        const { userDetailsData } = res?.data?.userDetails[0];
        startUserSession(userDetailsData);
        networkService.setAccessToken(res?.data?.userDetails[0]?.access);
        navigate("/dashboard");
      }
      else if (res?.error) {
        const responseData = res.error.response?.data;
        const activeDevices: RawSession[] = responseData?.active_devices || [];
        setSessionData(activeDevices);
        setSessionManagementToken(responseData?.temp_token || "");
        if (activeDevices.length >= MAX_ACTIVE_DEVICES) {
          setSessionModalOpen(true);
        } else {
          setLoginEmailMessage("Login failed. Please try again.");
        }
      }
    } catch (error) {
      setLoginEmailMessage("Login failed. Please try again.");
      setLoading(false);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSessionsChanged = (remaining: RawSession[]) => {
    setSessionData(remaining);
    if (remaining.length < MAX_ACTIVE_DEVICES) {
      setSessionModalOpen(false);
      handleLoginWithEmail();
    }
  };

  const handleForgotEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const emailForgotValue = event.target.value;
    setEmailIdForgot(emailForgotValue);

    if (!emailRegex.test(emailForgotValue)) {
      setForgotEmailMessage("Please Enter a Valid Email Id");
    } else {
      setForgotEmailMessage("");
    }
  };

  const handleSendOtp = async () => {
    if (!emailRegex.test(emailIdForgot)) {
      setForgotEmailMessage("Please Enter a Valid Email Id");
      return;
    }
    setForgotEmailMessage("");
    setLoading(true);
    const res = await sendPasswordResetOtp({
      email: emailIdForgot,
    });
    setLoading(false);
    if (res) {
      setForgotStep(2);
      setOtpMessage("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleConfirmPasswordReset = async () => {
    if (!otp.trim()) {
      setOtpMessage("Please Enter the OTP");
      return;
    }
    if (newPassword === "") {
      setNewPasswordMessage("Please Enter a New Password");
      return;
    }
    if (confirmPassword === "") {
      setConfirmPasswordMessage("Please Confirm the New Password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordMessage("Passwords do not match");
      return;
    }
    setOtpMessage("");
    setNewPasswordMessage("");
    setConfirmPasswordMessage("");
    setLoading(true);
    const res = await confirmPasswordReset({
      email: emailIdForgot,
      otp,
      new_password: newPassword,
    });
    setLoading(false);
    if (res) {
      setForgotPassword(false);
      setEmailLogin(true);
      setForgotStep(1);
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleBackToEmail = () => {
    setForgotStep(1);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setOtpMessage("");
    setNewPasswordMessage("");
    setConfirmPasswordMessage("");
  };

  return (
    <Box
      className="login-modal"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1300,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "800px",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ padding: 4, backgroundColor: "#fff" }}
          >
            <br />
            <Box
              sx={{
                display: "flex",
                marginBottom: 3,
              }}
            >
              <img
                src="https://uploads-ssl.webflow.com/63001a1f3b1b4d42d0ae9396/66977ffc615ef2b8f6931569_Vector.png"
                alt="Logo"
                style={{ height: "40px", width: "200px" }}
              />
            </Box>
            {emailLogin === true ? (
              <>
                {isForgotPassword === true ? (
                  forgotStep === 1 ? (
                    <>
                      <TextField
                        fullWidth
                        placeholder="Enter your email address"
                        variant="outlined"
                        label="Email Address"
                        value={emailIdForgot}
                        type="email"
                        onChange={handleForgotEmailChange}
                        error={!!errorForgotEmailMessage}
                        helperText={errorForgotEmailMessage}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email />
                            </InputAdornment>
                          ),
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSendOtp();
                          }
                        }}
                        sx={{
                          marginBottom: 2,
                          "& input[type=email]::-webkit-inner-spin-button": {
                            display: "none",
                          },
                          "& input[type=email]::-webkit-outer-spin-button": {
                            display: "none",
                          },
                        }}
                      />
                      <Button
                        onClick={handleSendOtp}
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{
                          background: `linear-gradient(to right,${colors.orange}, ${colors.red})`,
                          color: "white",
                        }}
                      >
                        {loading && (
                          <>
                            <Spinner color="primary" size="sm">
                              Loading...
                            </Spinner>
                            &nbsp;
                          </>
                        )} Send OTP
                      </Button>

                      <p
                        onClick={() => setForgotPassword(false)}
                        style={{
                          color: "rgb(29, 78, 216)",
                          marginTop: "10px",
                          cursor: "pointer",
                        }}
                      >
                        Login with Email
                      </p>
                    </>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        placeholder="Enter the OTP sent to your email"
                        variant="outlined"
                        label="OTP"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          setOtpMessage("");
                        }}
                        error={!!errorOtpMessage}
                        helperText={errorOtpMessage}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleConfirmPasswordReset();
                          }
                        }}
                        sx={{ marginBottom: 2 }}
                      />
                      <TextField
                        fullWidth
                        placeholder="Enter your new password"
                        variant="outlined"
                        label="New Password"
                        value={newPassword}
                        type="password"
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setNewPasswordMessage("");
                        }}
                        error={!!errorNewPasswordMessage}
                        helperText={errorNewPasswordMessage}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleConfirmPasswordReset();
                          }
                        }}
                        sx={{
                          marginBottom: 2,
                          "& input[type=password]::-webkit-inner-spin-button": {
                            display: "none",
                          },
                          "& input[type=password]::-webkit-outer-spin-button": {
                            display: "none",
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        placeholder="Re-enter your new password"
                        variant="outlined"
                        label="Confirm New Password"
                        value={confirmPassword}
                        type="password"
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setConfirmPasswordMessage("");
                        }}
                        error={!!errorConfirmPasswordMessage}
                        helperText={errorConfirmPasswordMessage}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleConfirmPasswordReset();
                          }
                        }}
                        sx={{
                          marginBottom: 2,
                          "& input[type=password]::-webkit-inner-spin-button": {
                            display: "none",
                          },
                          "& input[type=password]::-webkit-outer-spin-button": {
                            display: "none",
                          },
                        }}
                      />
                      <Button
                        onClick={handleConfirmPasswordReset}
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{
                          background: `linear-gradient(to right,${colors.orange}, ${colors.red})`,
                          color: "white",
                        }}
                      >
                        {loading && (
                          <>
                            <Spinner color="primary" size="sm">
                              Loading...
                            </Spinner>
                            &nbsp;
                          </>
                        )} Reset Password
                      </Button>

                      <p
                        onClick={handleBackToEmail}
                        style={{
                          color: "rgb(29, 78, 216)",
                          marginTop: "10px",
                          cursor: "pointer",
                        }}
                      >
                        Back to Email
                      </p>
                    </>
                  )
                ) : (
                  <>
                    <TextField
                      fullWidth
                      placeholder="Enter your email address"
                      variant="outlined"
                      label="Email Address"
                      value={emailId}
                      type="email"
                      onChange={handleEmailChange}
                      error={!!errorLoginEmailMessage}
                      helperText={errorLoginEmailMessage}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email />
                          </InputAdornment>
                        ),
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleLoginWithEmail();
                        }
                      }}
                      sx={{
                        marginBottom: 2,
                        "& input[type=email]::-webkit-inner-spin-button": {
                          display: "none",
                        },
                        "& input[type=email]::-webkit-outer-spin-button": {
                          display: "none",
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      placeholder="Enter your password"
                      variant="outlined"
                      label="Password"
                      value={password}
                      type="password"
                      onChange={handlePasswordChange}
                      error={!!errorLoginPasswordMessage}
                      helperText={errorLoginPasswordMessage}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Password />
                          </InputAdornment>
                        ),
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleLoginWithEmail();
                        }
                      }}
                      sx={{
                        marginBottom: 2,
                        "& input[type=password]::-webkit-inner-spin-button": {
                          display: "none",
                        },
                        "& input[type=password]::-webkit-outer-spin-button": {
                          display: "none",
                        },
                      }}
                    />
                    <Button
                      onClick={handleLoginWithEmail}
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{
                        background: `linear-gradient(to right,${colors.orange}, ${colors.red})`,
                        color: "white",
                      }}
                    >
                      {loading &&
                        <>
                          <Spinner color="primary" size="sm">
                            Loading...
                          </Spinner>
                          &nbsp;
                        </>
                      } Login
                    </Button>
                    <p
                      onClick={() => setForgotPassword(true)}
                      style={{
                        color: "rgb(29, 78, 216)",
                        marginTop: "10px",
                        marginBottom: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Forgot Password
                    </p>
                  </>
                )}
              </>
            ) : (
              <>

              </>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              padding: 4,
              backgroundColor: "#f5f5f5",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Redefine your fleet management
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ marginBottom: 3 }}
            >
              Welcome to Novae Avenue! Keep track of your vehicles, schedule
              maintenance, and monitor everything easily.
            </Typography>
            <img
              src="login_model.png"
              alt="Fleet management"
              style={{ width: "100%", borderRadius: "8px" }}
            />
          </Grid>
        </Grid>
      </Paper>

      {sessionModalOpen && (
        <ActiveLoginSessions
          open={sessionModalOpen}
          onClose={() => setSessionModalOpen(false)}
          sessions={sessionData}
          accessToken={sessionManagementToken}
          onSessionsChanged={handleSessionsChanged}
        />
      )}
    </Box>
  );
};

export default LoginModal;

// import React, { useEffect, useRef, useState } from "react";
// import {
//   Button,
//   TextField,
//   Typography,
//   Box,
//   Paper,
//   InputAdornment,
// } from "@mui/material";
// import Email from "@mui/icons-material/MailOutline";
// import Password from "@mui/icons-material/Key";
// import Grid from "@mui/material/Grid";
// import { LoginModalProps } from "../../interfaces/global-interfaces";
// import { colors } from "../../constants/colors";
// import {forgotPassword,loginWithEmail} from "../../redux/actions/auth-action";
// import { useDispatch } from "react-redux";
// import {
//   setIsLoggedIn,
//   setLoginDetails,
// } from "../../redux/reducers/auth-reducer";
// import { networkService } from "../../networking/network-services";
// import { setMobileNumber } from "../../redux/reducers/service-reducer";
// import { useNavigate } from "react-router-dom";
// import { Spinner } from "reactstrap";
// import { startUserSession } from "../../utils/analyticsTracker";

// const LoginModal: React.FC<LoginModalProps> = ({
//   onHide,
//   onUsernameUpdate,
// }) => {
//   const dispatch = useDispatch();
//   const [isForgotPassword, setForgotPassword] = React.useState<any>(false);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [mobileNumber, setStateMobileNumber] = React.useState("");
//   const [emailId, setEmailID] = React.useState("");
//   const [emailIdForgot, setEmailIdForgot] = React.useState("");
//   const [password, setPassword] = React.useState("");
//   const [errorForgotEmailMessage, setForgotEmailMessage] = React.useState("");
//   const [errorLoginEmailMessage, setLoginEmailMessage] = React.useState("");
//   const [errorLoginPasswordMessage, setLoginPasswordMessage] =React.useState("");
//   const [emailLogin, setEmailLogin] = React.useState(true);
//   const [resendTimer, setResendTimer] = useState<number>(120);
//   const navigate = useNavigate();

//   useEffect(() => {
//     if (resendTimer > 0) {
//       const timerId = setTimeout(() => {
//         setResendTimer((prevTime) => prevTime - 1);
//       }, 1000);
//       return () => clearTimeout(timerId);
//     }
//   }, [resendTimer]);


//   const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

//   const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const emailValue = event.target.value;
//     setEmailID(emailValue);

//     if (!emailRegex.test(emailValue)) {
//       setLoginEmailMessage("Please Enter a Valid Email Id");
//     } else {
//       setLoginEmailMessage("");
//     }
//   };

//   const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const passwordValue = event.target.value;
//     if (passwordValue === "") {
//       setLoginPasswordMessage("Please Enter a Password");
//     } else {
//       setLoginPasswordMessage("");
//     }
//     setPassword(passwordValue);
//   };

//   const handleLoginWithEmail = async () => {
//     setLoading(true);
//     try {
//       if (!emailRegex.test(emailId)) {
//         setLoginEmailMessage("Please Enter a Valid Email Id");
//         setLoading(false);
//       }
//       if (password === "") {
//         setLoginPasswordMessage("Please Enter a Password");
//         setLoading(false);
//         return;
//       }
//       const res = await loginWithEmail({ email: emailId, password });
//       if (res.status === 200) {
//         setLoading(false);
//         localStorage.setItem("loginDate", new Date().toISOString().split("T")[0]);
//         dispatch(setIsLoggedIn(true));
//         dispatch(setLoginDetails(res?.data));
//         dispatch(setMobileNumber(mobileNumber));
//         const { userDetailsData } = res?.data?.userDetails[0];
//         startUserSession(userDetailsData);
//         networkService.setAccessToken(res?.data?.userDetails[0]?.access);
//         navigate("/dashboard");
//       }
//     } catch (error) {
//       setLoginEmailMessage("Login failed. Please try again.");
//       setLoading(false);
//     }
//     finally {
//       setLoading(false);
//     }
//   };

//   const handleForgotEmailChange = (
//     event: React.ChangeEvent<HTMLInputElement>
//   ) => {
//     const emailForgotValue = event.target.value;
//     setEmailIdForgot(emailForgotValue);

//     if (!emailRegex.test(emailForgotValue)) {
//       setForgotEmailMessage("Please Enter a Valid Email Id");
//     } else {
//       setForgotEmailMessage("");
//     }
//   };

//   const handleForgotPassword = async () => {
//     if (!emailRegex.test(emailIdForgot)) {
//       setForgotEmailMessage("Please Enter a Valid Email Id");
//     } else {
//       setForgotEmailMessage("");
//       const res = await forgotPassword({
//         email: emailIdForgot,
//       });
//       if (res) {
//         setForgotPassword(false);
//         setEmailLogin(true);
//       }
//     }
//   };

//   return (
//     <Box
//       className="login-modal"
//       sx={{
//         position: "fixed",
//         top: 0,
//         left: 0,
//         width: "100%",
//         height: "100%",
//         backgroundColor: "rgba(0, 0, 0, 0.5)",
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         zIndex: 1300,
//       }}
//     >
//       <Paper
//         elevation={3}
//         sx={{
//           display: "flex",
//           width: "100%",
//           maxWidth: "800px",
//           borderRadius: 2,
//           overflow: "hidden",
//         }}
//       >
//         <Grid container>
//           <Grid
//             item
//             xs={12}
//             md={6}
//             sx={{ padding: 4, backgroundColor: "#fff" }}
//           >
//             <br />
//             <Box
//               sx={{
//                 display: "flex",
//                 marginBottom: 3,
//               }}
//             >
//               <img
//                 src="https://uploads-ssl.webflow.com/63001a1f3b1b4d42d0ae9396/66977ffc615ef2b8f6931569_Vector.png"
//                 alt="Logo"
//                 style={{ height: "40px", width: "200px" }}
//               />
//             </Box>
//             {emailLogin === true ? (
//               <>
//                 {isForgotPassword === true ? (
//                   <>
//                     <TextField
//                       fullWidth
//                       placeholder="Enter your email address"
//                       variant="outlined"
//                       label="Email Address"
//                       value={emailIdForgot}
//                       type="email"
//                       onChange={handleForgotEmailChange}
//                       error={!!errorForgotEmailMessage}
//                       helperText={errorForgotEmailMessage}
//                       InputProps={{
//                         startAdornment: (
//                           <InputAdornment position="start">
//                             <Email />
//                           </InputAdornment>
//                         ),
//                       }}
//                       onKeyDown={(e) => {
//                         if (e.key === "Enter") {
//                           handleForgotPassword();
//                         }
//                       }}
//                       sx={{
//                         marginBottom: 2,
//                         "& input[type=email]::-webkit-inner-spin-button": {
//                           display: "none",
//                         },
//                         "& input[type=email]::-webkit-outer-spin-button": {
//                           display: "none",
//                         },
//                       }}
//                     />
//                     <Button
//                       onClick={handleForgotPassword}
//                       fullWidth
//                       variant="contained"
//                       color="primary"
//                       sx={{
//                         background: `linear-gradient(to right,${colors.orange}, ${colors.red})`,
//                         color: "white",
//                       }}
//                     >
//                       Submit
//                     </Button>

//                     <p
//                       onClick={() => setForgotPassword(false)}
//                       style={{
//                         color: "rgb(29, 78, 216)",
//                         marginTop: "10px",
//                         cursor: "pointer",
//                       }}
//                     >
//                       Login with Email
//                     </p>
//                   </>
//                 ) : (
//                   <>
//                     <TextField
//                       fullWidth
//                       placeholder="Enter your email address"
//                       variant="outlined"
//                       label="Email Address"
//                       value={emailId}
//                       type="email"
//                       onChange={handleEmailChange}
//                       error={!!errorLoginEmailMessage}
//                       helperText={errorLoginEmailMessage}
//                       InputProps={{
//                         startAdornment: (
//                           <InputAdornment position="start">
//                             <Email />
//                           </InputAdornment>
//                         ),
//                       }}
//                       onKeyDown={(e) => {
//                         if (e.key === "Enter") {
//                           handleLoginWithEmail();
//                         }
//                       }}
//                       sx={{
//                         marginBottom: 2,
//                         "& input[type=email]::-webkit-inner-spin-button": {
//                           display: "none",
//                         },
//                         "& input[type=email]::-webkit-outer-spin-button": {
//                           display: "none",
//                         },
//                       }}
//                     />
//                     <TextField
//                       fullWidth
//                       placeholder="Enter your password"
//                       variant="outlined"
//                       label="Password"
//                       value={password}
//                       type="password"
//                       onChange={handlePasswordChange}
//                       error={!!errorLoginPasswordMessage}
//                       helperText={errorLoginPasswordMessage}
//                       InputProps={{
//                         startAdornment: (
//                           <InputAdornment position="start">
//                             <Password />
//                           </InputAdornment>
//                         ),
//                       }}
//                       onKeyDown={(e) => {
//                         if (e.key === "Enter") {
//                           handleLoginWithEmail();
//                         }
//                       }}
//                       sx={{
//                         marginBottom: 2,
//                         "& input[type=password]::-webkit-inner-spin-button": {
//                           display: "none",
//                         },
//                         "& input[type=password]::-webkit-outer-spin-button": {
//                           display: "none",
//                         },
//                       }}
//                     />
//                     <Button
//                       onClick={handleLoginWithEmail}
//                       fullWidth
//                       variant="contained"
//                       color="primary"
//                       sx={{
//                         background: `linear-gradient(to right,${colors.orange}, ${colors.red})`,
//                         color: "white",
//                       }}
//                     >
//                       {loading &&
//                         <>
//                           <Spinner color="primary" size="sm">
//                             Loading...
//                           </Spinner>
//                           &nbsp;
//                         </>
//                       } Login
//                     </Button>
//                     <p
//                       onClick={() => setForgotPassword(true)}
//                       style={{
//                         color: "rgb(29, 78, 216)",
//                         marginTop: "10px",
//                         marginBottom: "5px",
//                         cursor: "pointer",
//                       }}
//                     >
//                       Forgot Password
//                     </p>
//                   </>
//                 )}
//               </>
//             ) : (
//               <>
                
//               </>
//             )}
//           </Grid>
//           <Grid
//             item
//             xs={12}
//             md={6}
//             sx={{
//               padding: 4,
//               backgroundColor: "#f5f5f5",
//               display: "flex",
//               flexDirection: "column",
//               justifyContent: "center",
//             }}
//           >
//             <Typography variant="h6" gutterBottom>
//               Redefine your fleet management
//             </Typography>
//             <Typography
//               variant="body1"
//               color="textSecondary"
//               sx={{ marginBottom: 3 }}
//             >
//               Welcome to Novae Avenue! Keep track of your vehicles, schedule
//               maintenance, and monitor everything easily.
//             </Typography>
//             <img
//               src="login_model.png"
//               alt="Fleet management"
//               style={{ width: "100%", borderRadius: "8px" }}
//             />
//           </Grid>
//         </Grid>
//       </Paper>
//     </Box>
//   );
// };

// export default LoginModal;
